import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { QRCodeCanvas } from 'qrcode.react';
import Icon from '../components/Icon';
import './Portail.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(`${d}T12:00:00`).toLocaleDateString('fr-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

function fmt$(v) {
  if (v == null) return '—';
  return parseFloat(v).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });
}

function genererICS(commande) {
  if (!commande?.date_event || !commande?.slot_pickup) return;
  const [heure, minute] = commande.slot_pickup.split(':').map(Number);
  const dateStr = commande.date_event.replace(/-/g, '');
  const debut = `${String(heure).padStart(2, '0')}${String(minute || 0).padStart(2, '0')}00`;
  const fin = `${String(heure + 1).padStart(2, '0')}${String(minute || 0).padStart(2, '0')}00`;
  const location = [commande.lieu, commande.ville].filter(Boolean).join(', ');
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Escamel//Billet//FR',
    'BEGIN:VEVENT',
    `UID:${commande.numero_conf}@escamel.ca`,
    `DTSTART:${dateStr}T${debut}`,
    `DTEND:${dateStr}T${fin}`,
    `SUMMARY:Pickup Escamel - ${commande.evenement_nom}`,
    `LOCATION:${location}`,
    `DESCRIPTION:Commande ${commande.numero_conf}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `escamel-${commande.numero_conf}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

function urlGoogleMaps(commande) {
  const q = [commande?.adresse, commande?.ville, 'Québec Canada'].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

const STATUTS = {
  confirmee: { label: 'Confirmée', cls: 'badge-success', idx: 1 },
  prete: { label: 'Prête', cls: 'badge-gold', idx: 2 },
  remise: { label: 'Remise', cls: 'badge-muted', idx: 3 },
  annulee: { label: 'Annulée', cls: 'badge-danger', idx: -1 },
  en_attente: { label: 'En attente', cls: 'badge-muted', idx: 0 },
};

const ETAPES_STATUT = [
  { key: 'confirmee', label: 'Confirmée' },
  { key: 'prete', label: 'Prête' },
  { key: 'remise', label: 'Remise' },
];

function StepperStatut({ statut }) {
  const idxActuel = (STATUTS[statut] || STATUTS.en_attente).idx;
  return (
    <div className="stepper-statut">
      {ETAPES_STATUT.map((etape, index) => {
        const idx = index + 1;
        const fait = idxActuel > idx;
        const actuel = idxActuel === idx;
        return (
          <React.Fragment key={etape.key}>
            <div className={`step-item ${fait ? 'step-fait' : actuel ? 'step-actuel' : ''}`}>
              <div className="step-cercle">
                {fait ? <Icon name="check" size={14} weight="bold" /> : idx}
              </div>
              <div className="step-label">{etape.label}</div>
              {actuel && <div className="step-ici-badge">vous êtes ici</div>}
            </div>
            {index < ETAPES_STATUT.length - 1 && <div className={`step-trait ${fait ? 'trait-fait' : ''}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ModalModification({ token, commande, onClose, onSuccess }) {
  const [plateaux, setPlateaux] = useState([]);
  const [selection, setSelection] = useState({});
  const [chargement, setChargement] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    async function charger() {
      setChargement(true);
      try {
        const { data } = await axios.get(`${API}/api/public/commande/${token}/modifier`);
        setPlateaux(Array.isArray(data.plateaux) ? data.plateaux : []);
        const init = {};
        (data.items_actuels || []).forEach((item) => {
          const key = item.plateau_id || item.id || item.taille;
          init[key] = item.quantite || item.qte || 1;
        });
        setSelection(init);
      } catch (err) {
        setErreur(err.response?.data?.error || 'Modification indisponible.');
      } finally {
        setChargement(false);
      }
    }
    charger();
  }, [token]);

  const totalNouveau = plateaux.reduce((sum, plateau) => sum + ((selection[plateau.id] || 0) * plateau.prix), 0);
  const diff = totalNouveau - parseFloat(commande.montant_total || 0);

  async function sauvegarder() {
    setSaving(true);
    setErreur('');
    try {
      const nouveaux_items = plateaux
        .filter((plateau) => (selection[plateau.id] || 0) > 0)
        .map((plateau) => ({
          plateau_id: plateau.id,
          taille: plateau.taille,
          label: plateau.label,
          quantite: selection[plateau.id],
          prix_unitaire: plateau.prix,
        }));
      await axios.post(`${API}/api/public/commande/${token}/modifier`, { nouveaux_items });
      onSuccess();
      onClose();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de la modification.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-titre">Modifier ma commande</div>
          <button className="modal-close" onClick={onClose} aria-label="Fermer">
            <Icon name="fermer" size={16} />
          </button>
        </div>
        <div className="modal-body">
          {chargement ? (
            <div className="muted">Chargement…</div>
          ) : (
            <>
              {plateaux.map((plateau) => (
                <div key={plateau.id} className="modif-plateau-row">
                  <div>
                    <div className="modif-plateau-label">{plateau.label}</div>
                    <div className="modif-plateau-prix muted">{fmt$(plateau.prix)}</div>
                  </div>
                  <div className="plateau-qte">
                    <button className="qte-btn" onClick={() => setSelection((prev) => ({ ...prev, [plateau.id]: Math.max(0, (prev[plateau.id] || 0) - 1) }))}>−</button>
                    <span className="qte-val">{selection[plateau.id] || 0}</span>
                    <button className="qte-btn" onClick={() => setSelection((prev) => ({ ...prev, [plateau.id]: (prev[plateau.id] || 0) + 1 }))}>+</button>
                  </div>
                </div>
              ))}
              <div className="modif-total">
                <div>Nouveau total : <strong className="gold">{fmt$(totalNouveau)}</strong></div>
                <div className="muted">{diff >= 0 ? `Supplément : ${fmt$(diff)}` : `Remboursement : ${fmt$(Math.abs(diff))}`}</div>
              </div>
              {erreur && <div className="etape-erreur">{erreur}</div>}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={sauvegarder} disabled={saving || chargement}>
            {saving ? 'Traitement…' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Portail() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const confirmed = params.get('confirmed') === '1';

  const [commande, setCommande] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [modalModification, setModalModification] = useState(false);
  const [msgSucces, setMsgSucces] = useState('');
  const ancienStatutRef = useRef(null);

  const charger = useCallback(async () => {
    if (!token) {
      setErreur('Lien invalide — aucun token fourni.');
      setChargement(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/public/commande/${token}`);
      const normalisee = {
        ...data,
        items: Array.isArray(data.items) ? data.items : [],
        extras: Array.isArray(data.extras) ? data.extras : [],
        allergies: Array.isArray(data.allergies) ? data.allergies : [],
        boissons_choisies: Array.isArray(data.boissons_choisies) ? data.boissons_choisies : [],
      };

      if (ancienStatutRef.current && ancienStatutRef.current !== 'prete' && normalisee.statut === 'prete' && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('Escamel — Votre commande est prête', {
          body: 'Présentez votre billet QR au comptoir.',
          icon: '/logo192.png',
        });
      }

      ancienStatutRef.current = normalisee.statut;
      setCommande(normalisee);
    } catch (err) {
      setErreur(err.response?.status === 404
        ? 'Commande introuvable. Vérifiez votre lien.'
        : 'Impossible de charger votre commande.');
    } finally {
      setChargement(false);
    }
  }, [token]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    if (!confirmed) return;
    confetti({
      particleCount: 150,
      spread: 80,
      colors: ['#c8941a', '#d4a017', '#f0ebe0', '#1a1814'],
    });
  }, [confirmed]);

  useEffect(() => {
    if (!commande || commande.statut === 'remise' || commande.statut === 'annulee') return;
    // Polling avec jitter pour éviter pic synchronisé sur le serveur
    let timeout;
    const planifier = () => {
      const delai = 25000 + Math.floor(Math.random() * 10000); // 25–35 s
      timeout = setTimeout(async () => { await charger(); planifier(); }, delai);
    };
    planifier();
    return () => clearTimeout(timeout);
  }, [commande, charger]);

  function modificationAutorisee() {
    if (!commande?.date_event) return false;
    const limite = new Date(`${commande.date_event}T12:00:00`).getTime() - (48 * 3600 * 1000);
    return Date.now() < limite && !['annulee', 'remise'].includes(commande.statut);
  }

  async function telechargerBillet() {
    const canvas = document.getElementById('qr-canvas');
    if (!canvas || !commande) return;
    const qrUrl = canvas.toDataURL('image/png');
    const html2pdf = (await import('html2pdf.js')).default;
    const billet = document.createElement('div');
    billet.innerHTML = `
      <div style="font-family: Georgia, serif; padding: 24px; border: 2px solid #c8941a; max-width: 420px; background: #faf7f2; color: #1a1814;">
        <h2 style="color: #c8941a; font-family: Cinzel, serif; margin-top: 0;">ESCAMEL BBQ</h2>
        <div style="text-align: center; margin: 20px 0;"><img src="${qrUrl}" width="200" /></div>
        <p><strong>${commande.numero_conf}</strong></p>
        <p>${commande.client_prenom} ${commande.client_nom}</p>
        <p>Créneau : ${commande.slot_pickup}</p>
        <p>${commande.evenement_nom}</p>
        <p>${fmtDate(commande.date_event)}</p>
      </div>
    `;
    await html2pdf().from(billet).save(`billet-${commande.numero_conf}.pdf`);
  }

  if (chargement) {
    return (
      <div className="portail-chargement">
        <div className="portail-logo-chargement">ESCAMEL</div>
        <div className="spinner-grand" />
        <div className="muted">Chargement de votre commande…</div>
      </div>
    );
  }

  if (erreur) {
    return (
      <div className="portail-erreur">
        <div className="portail-logo-chargement">ESCAMEL</div>
        <div className="erreur-icone-wrap">
          <Icon name="envelope" size={28} weight="regular" />
        </div>
        <div className="erreur-msg">{erreur}</div>
      </div>
    );
  }

  const qrValue = `ESCAMEL-CMD-${commande.numero_conf}`;
  const totalExtras = (commande.extras || []).reduce((sum, extra) => sum + ((extra.qte || extra.quantite || 0) * (extra.prix_unitaire || 0)), 0);

  return (
    <div className="portail-page">
      {msgSucces && (
        <div className="banniere-succes">
          <Icon name="succes" size={16} weight="fill" />
          {msgSucces}
        </div>
      )}

      <div className="portail-header">
        <div className="portail-logo">ESCAMEL</div>
        <div className="portail-sous-titre">Artisans du Fumoir</div>
      </div>

      <main className="portail-main">
        <section className="confirm-hero">
          <div className="confirm-hero-lbl gold">Réservation confirmée</div>
          <div className="confirm-num">{commande.numero_conf}</div>
          <div className="muted">Merci {commande.client_prenom}, votre place est réservée.</div>
          <span className={`badge ${STATUTS[commande.statut]?.cls || 'badge-muted'} badge-statut`}>
            {STATUTS[commande.statut]?.label || commande.statut}
          </span>
        </section>

        <div className="portail-card portail-card-statut">
          <div className="portail-card-titre muted">Statut</div>
          <StepperStatut statut={commande.statut} />
        </div>

        <div className="portail-card portail-card-qr">
          <div className="portail-card-titre">
            <Icon name="billet" size={14} weight="regular" />
            Votre billet
          </div>
          <p className="muted">Présentez ce code à notre équipe le jour J.</p>
          <div className="qr-wrap qr-wrap-vertical">
            <div className="qr-code-box">
              <QRCodeCanvas
                id="qr-canvas"
                value={qrValue}
                size={200}
                bgColor="#ffffff"
                fgColor="#1a1814"
                level="H"
                includeMargin
              />
            </div>
            <div className="qr-infos qr-infos-centre">
              <div className="qr-num">{commande.numero_conf}</div>
              <div className="qr-client">{commande.client_prenom} {commande.client_nom}</div>
              <div className="qr-slot">{commande.slot_pickup}</div>
            </div>
          </div>
          <button className="btn btn-ghost qr-download-btn" onClick={telechargerBillet}>
            <Icon name="telecharger" size={14} />
            Télécharger mon billet
          </button>
        </div>

        <div className="portail-card">
          <div className="portail-card-titre">
            <Icon name="package" size={14} />
            Votre commande
          </div>
          {(commande.items || []).map((item, index) => (
            <div key={index} className="portail-item">
              <div className="portail-item-label">{item.quantite || item.qte || 1}× {item.label || item.taille}</div>
              <div className="portail-item-prix">{fmt$((item.quantite || item.qte || 1) * (item.prix_unitaire || 0))}</div>
            </div>
          ))}
          {(commande.extras || []).map((extra, index) => (
            <div key={`extra-${index}`} className="portail-item">
              <div className="portail-item-label">{extra.qte || extra.quantite || 0}× {extra.nom}</div>
              <div className="portail-item-prix">{fmt$((extra.qte || extra.quantite || 0) * (extra.prix_unitaire || 0))}</div>
            </div>
          ))}
          <div className="portail-total">
            <span>Total</span>
            <span className="portail-total-montant gold">{fmt$(commande.montant_total || totalExtras)}</span>
            <span className="badge badge-success">Payé</span>
          </div>
        </div>

        {(commande.boissons_choisies || []).length > 0 && (
          <div className="portail-card">
            <div className="portail-card-titre">
              <Icon name="repas" size={14} />
              Vos boissons
            </div>
            <ul className="sides-liste">
              {commande.boissons_choisies.map((boisson, index) => (
                <li key={index} className="side-item">Personne {boisson.personne} : {boisson.boisson}</li>
              ))}
            </ul>
          </div>
        )}

        {(commande.allergies || []).length > 0 && (
          <div className="portail-card">
            <div className="portail-card-titre">
              <Icon name="alerte" size={14} />
              Allergies déclarées
            </div>
            <div className="allergies-wrap">
              {(commande.allergies || []).map((allergie) => (
                <span key={allergie} className="badge badge-danger">{allergie}</span>
              ))}
            </div>
            {commande.notes_allergies && <p className="muted" style={{ marginTop: 8 }}>{commande.notes_allergies}</p>}
          </div>
        )}

        <div className="portail-card portail-card-slot">
          <div className="portail-card-titre">
            <Icon name="date" size={14} />
            {commande.evenement_nom}
          </div>
          <div className="portail-ev-detail">{fmtDate(commande.date_event)}</div>
          <div className="slot-pickup-heure">{commande.slot_pickup}</div>
          <div className="portail-ev-detail">
            <Icon name="lieu" size={13} color="var(--muted)" />
            {commande.lieu}{commande.ville ? `, ${commande.ville}` : ''}
          </div>
        </div>

        <div className="portail-actions">
          <button className="btn btn-ghost portail-action-btn" onClick={() => genererICS(commande)}>
            <Icon name="date" size={14} />
            Ajouter au calendrier
          </button>
          <a href={urlGoogleMaps(commande)} target="_blank" rel="noopener noreferrer" className="btn btn-ghost portail-action-btn">
            <Icon name="lieu" size={14} />
            Directions
          </a>
        </div>

        {commande.notes_client && (
          <div className="portail-card">
            <div className="portail-card-titre">
              <Icon name="message" size={14} />
              Notes
            </div>
            <p className="muted" style={{ fontStyle: 'italic' }}>"{commande.notes_client}"</p>
          </div>
        )}

        {modificationAutorisee() && (
          <div className="portail-card">
            <div className="portail-card-titre">
              <Icon name="modifier" size={14} />
              Modifier ma commande
            </div>
            <button className="btn btn-ghost" onClick={() => setModalModification(true)}>
              <Icon name="modifier" size={14} />
              Modifier mes plateaux
            </button>
          </div>
        )}

        <div className="portail-contact muted">
          Un email de confirmation a été envoyé à {commande.client_email}
        </div>
      </main>

      {modalModification && (
        <ModalModification
          token={token}
          commande={commande}
          onClose={() => setModalModification(false)}
          onSuccess={() => {
            setMsgSucces('Commande modifiée avec succès.');
            charger();
            setTimeout(() => setMsgSucces(''), 5000);
          }}
        />
      )}
    </div>
  );
}
